'use client';
import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

interface Testimonial {
  quote: string;
  name: string;
  role: string;
  outcome: string;
  avatar: string;
  alt: string;
}

const testimonials: Testimonial[] = [
{
  quote: "I'd been a Staff Engineer for four years. Pivot diagnosed the problem in 45 minutes: I was presenting my work as execution, not strategy. Three months later I had two VP-level offers.",
  name: "Marcus Chen",
  role: "VP of Engineering, Series B SaaS",
  outcome: "+$47K total comp",
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_14a5ca983-1763300171126.png",
  alt: "Marcus Chen, VP of Engineering, looking confident in professional attire"
},
{
  quote: "After 18 months of job searching post-layoff, I had one session with Pivot and rewrote my entire narrative. I had three callbacks the following week from companies I'd already applied to.",
  name: "Diane Okonkwo",
  role: "Director of Product, FinTech",
  outcome: "3 callbacks in 7 days",
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1417554f4-1772059811158.png",
  alt: "Diane Okonkwo, Director of Product, smiling in a professional setting"
},
{
  quote: "I knew I was undervalued. I couldn't articulate why until the résumé teardown. My experience was buried under job-description language that made me sound interchangeable.",
  name: "Rafael Torres",
  role: "Senior Data Scientist → ML Lead",
  outcome: "+$38K + equity bump",
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_14a5ca983-1763300171126.png",
  alt: "Rafael Torres, ML Lead, in a thoughtful pose wearing business casual"
},
{
  quote: "The mock interview session was uncomfortable in the best way. I heard myself underselling scope in real time. The written debrief told me exactly what to change and why.",
  name: "Priya Anand",
  role: "Senior Manager → Head of Operations",
  outcome: "Offer in 6 weeks",
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1a843987d-1763296467583.png",
  alt: "Priya Anand, Head of Operations, professional headshot with warm expression"
},
{
  quote: "Career-switching at 38 felt like starting over. Pivot showed me that every year of my background was transferable I just needed the translation layer that makes that obvious to a hiring manager.",
  name: "James Whitfield",
  role: "Finance → Head of Strategy",
  outcome: "Role landed in 11 weeks",
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_174ead5c8-1772167306381.png",
  alt: "James Whitfield, Head of Strategy, confident expression in business attire"
},
{
  quote: "I'd been passed over twice for Director. The problem wasn't my performance it was that my manager couldn't repeat my value proposition in a room I wasn't in. Pivot fixed that.",
  name: "Sophia Nakamura",
  role: "Director of Design, Enterprise SaaS",
  outcome: "Promoted within 4 months",
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1c20d6ce4-1763294854382.png",
  alt: "Sophia Nakamura, Director of Design, professional photo with creative background"
}];


function TestimonialCard({ t }: {t: Testimonial;}) {
  return (
    <div className="testimonial-card rounded-sm p-7 w-[380px] flex-shrink-0 flex flex-col justify-between cursor-default">
      <p className="font-fraunces text-base font-light italic leading-relaxed mb-6" style={{ color: 'rgba(245,240,232,0.85)' }}>
        "{t.quote}"
      </p>
      <div>
        <div className="gold-rule-full opacity-10 mb-5" />
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 border border-gold/20">
            <AppImage src={t.avatar} alt={t.alt} width={40} height={40} className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate" style={{ color: '#F5F0E8' }}>{t.name}</div>
            <div className="text-xs truncate" style={{ color: 'rgba(245,240,232,0.5)' }}>{t.role}</div>
          </div>
          <div
            className="flex-shrink-0 text-xs font-mono-custom font-medium px-2 py-1 rounded-sm"
            style={{ background: 'rgba(201,168,76,0.12)', color: '#C9A84C', border: '1px solid rgba(201,168,76,0.2)' }}>
            
            {t.outcome}
          </div>
        </div>
      </div>
    </div>);

}

export default function TestimonialsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-visible');
        });
      },
      { threshold: 0.1 }
    );
    if (headRef.current) observer.observe(headRef.current);
    return () => observer.disconnect();
  }, []);

  const doubled = [...testimonials, ...testimonials];

  return (
    <section className="section-plum relative py-24 lg:py-32 overflow-hidden" id="testimonials">
      <div className="noise-overlay" />

      <div className="relative z-10">
        {/* Header */}
        <div ref={headRef} className="reveal-section max-w-7xl mx-auto px-6 lg:px-12 mb-14">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <span className="section-index block mb-4" style={{ color: 'rgba(201,168,76,0.5)' }}>
                04 / CLIENT OUTCOMES
              </span>
              <h2
                className="font-fraunces font-medium"
                style={{ fontSize: 'clamp(2rem, 4.5vw, 3.5rem)', color: '#F5F0E8', lineHeight: '1.05' }}>
                
                The case file speaks<br />
                <span style={{ color: '#C9A84C', fontStyle: 'italic' }}>for itself.</span>
              </h2>
            </div>
            <div className="flex flex-col gap-3">
              <a
                href="#booking"
                className="cta-gold px-7 py-3.5 rounded-sm text-sm font-semibold tracking-wide inline-flex items-center gap-2">
                
                Book Your Strategy Call
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
              <a href="#audit" className="cta-secondary text-sm font-medium text-center">
                Download Career Audit Checklist
              </a>
            </div>
          </div>
        </div>

        {/* Marquee rows */}
        <div className="flex flex-col gap-6 group">
          {/* Fade masks */}
          <div className="absolute inset-y-0 left-0 w-24 z-20 pointer-events-none" style={{ background: 'linear-gradient(to right, #4A1942, transparent)' }} />
          <div className="absolute inset-y-0 right-0 w-24 z-20 pointer-events-none" style={{ background: 'linear-gradient(to left, #4A1942, transparent)' }} />

          {/* Row 1 left */}
          <div className="overflow-hidden marquee-track">
            <div className="flex gap-5 marquee-left w-max px-5 py-2">
              {doubled.map((t, i) =>
              <TestimonialCard key={`r1-${i}`} t={t} />
              )}
            </div>
          </div>

          {/* Row 2 right */}
          <div className="overflow-hidden marquee-track">
            <div className="flex gap-5 marquee-right w-max px-5 py-2">
              {[...doubled].reverse().map((t, i) =>
              <TestimonialCard key={`r2-${i}`} t={t} />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>);

}